import React, { useEffect, useState } from "react";
import "./AccessibilityButton.scss";

type ThemeMode = "light" | "dark";

type AccessibilitySettings = {
  greyscale: boolean;
  theme: ThemeMode;
  fontScale: number;
};

const STORAGE_KEY = "turbo-accessibility-settings";
const MIN_FONT_SCALE = 90;
const MAX_FONT_SCALE = 120;
const FONT_STEP = 10;

const defaultSettings: AccessibilitySettings = {
  greyscale: false,
  theme: "light",
  fontScale: 100,
};

const getSavedSettings = (): AccessibilitySettings => {
  if (typeof window === "undefined") {
    return defaultSettings;
  }

  try {
    const savedSettings = window.localStorage.getItem(STORAGE_KEY);
    if (!savedSettings) {
      return defaultSettings;
    }

    return {
      ...defaultSettings,
      ...JSON.parse(savedSettings),
    };
  } catch {
    return defaultSettings;
  }
};

const AccessibilityButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState<AccessibilitySettings>(getSavedSettings);

  useEffect(() => {
    const root = document.documentElement;

    root.classList.toggle("a11y-greyscale", settings.greyscale);
    root.classList.toggle("a11y-dark-theme", settings.theme === "dark");
    root.classList.toggle("a11y-light-theme", settings.theme === "light");
    root.classList.toggle("a11y-font-adjust", settings.fontScale !== 100);
    root.style.setProperty("--a11y-font-scale", `${settings.fontScale}%`);
    root.style.setProperty("--a11y-font-ratio", `${settings.fontScale / 100}`);

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const toggleGreyscale = () => {
    setSettings((current) => ({
      ...current,
      greyscale: !current.greyscale,
    }));
  };

  const toggleTheme = () => {
    setSettings((current) => ({
      ...current,
      theme: current.theme === "dark" ? "light" : "dark",
    }));
  };

  const updateFontScale = (direction: "increase" | "decrease") => {
    setSettings((current) => {
      const nextScale = direction === "increase"
        ? Math.min(current.fontScale + FONT_STEP, MAX_FONT_SCALE)
        : Math.max(current.fontScale - FONT_STEP, MIN_FONT_SCALE);

      return {
        ...current,
        fontScale: nextScale,
      };
    });
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
  };

  return (
    <div className="accessibility-widget">
      <button
        type="button"
        className="accessibility-widget__trigger"
        aria-expanded={isOpen}
        aria-controls="accessibility-controls"
        onClick={() => setIsOpen((current) => !current)}
      >
        <svg fill="#ffffff" width="30px" height="30px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M9.5,3.5A2.5,2.5,0,1,1,12,6,2.5,2.5,0,0,1,9.5,3.5ZM20,7H4A1,1,0,0,0,4,9H9V22a1,1,0,0,0,2,0V15h2v7a1,1,0,0,0,2,0V9h5a1,1,0,0,0,0-2Z"/></svg>
      </button>

      {isOpen && (
        <div
          id="accessibility-controls"
          className="accessibility-widget__panel"
          role="group"
          aria-label="Accessibility controls"
        >
          <button
            type="button"
            className={settings.greyscale ? "is-active" : ""}
            aria-pressed={settings.greyscale}
            onClick={toggleGreyscale}
          >
            Greyscale
          </button>

          <button
            type="button"
            className={settings.theme === "dark" ? "is-active" : ""}
            aria-pressed={settings.theme === "dark"}
            onClick={toggleTheme}
          >
            {settings.theme === "dark" ? "Light theme" : "Dark theme"}
          </button>

          <div className="accessibility-widget__font-controls" aria-label="Font size controls">
            <button
              type="button"
              aria-label="Decrease font size"
              disabled={settings.fontScale === MIN_FONT_SCALE}
              onClick={() => updateFontScale("decrease")}
            >
              A-
            </button>
            <span aria-live="polite">{settings.fontScale}%</span>
            <button
              type="button"
              aria-label="Increase font size"
              disabled={settings.fontScale === MAX_FONT_SCALE}
              onClick={() => updateFontScale("increase")}
            >
              A+
            </button>
          </div>

          <button type="button" onClick={resetSettings}>
            Reset
          </button>
        </div>
      )}
    </div>
  );
};

export default AccessibilityButton;
